import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Tooltip,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import srLocale from "date-fns/locale/sr";
import {
  editNatMeasure,
  updateRealisationDate,
  updateRealisationStatusNote,
} from "../../../../server/server";
import { parse, isValid, set, addMonths, subMonths } from "date-fns";
import {
  getCurrentAndPreviousQuarter,
  getFirstDayOfCurrentQuarter,
  getQuarterDateRange,
  notif,
} from "../../../utils/util";
import ConfirmationModal from "../../../modals/ConfirmationModal";
import RealisationNotesTableModal from "./RealisationNotesTableModal";

const MIN_YEAR = 1900;

interface EditRealisationModalProps {
  open: boolean;
  register: any;
  onClose: () => void;
  onSavedRealisation?: (register: any) => void;
  setTempRealisation?: any;
  callback: () => void;
  closeModal: () => void;
  setNoteData?: any;
  quarter?: string;
  disabled?: boolean;
}

const EditRealisationModal: React.FC<EditRealisationModalProps> = ({
  open,
  register,
  onClose,
  onSavedRealisation,
  setTempRealisation,
  callback,
  closeModal,
  setNoteData,
  quarter,
  disabled,
}) => {
  const [status, setStatus] = useState<string>("");
  const [date, setDate] = useState<Date | null>(null);
  const [showConfirmation, setShowConfirmation] = useState<boolean>(false);
  const [selectedOption, setSelectedOption] = useState<string>("");
  const [note, setNote] = useState<string>("");
  const [noteId, setNoteId] = useState<number | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);

  const newRealisation = register.status !== "Реализовано" ? true : false;

  const effectiveQuarter =
    quarter && quarter.trim() !== ""
      ? quarter
      : getCurrentAndPreviousQuarter().current;

  const { minDate: quarterMinDate, maxDate: quarterMaxDate } =
    getQuarterDateRange(effectiveQuarter);

  const extendedMinDate = subMonths(quarterMinDate, 2);
  const extendedMaxDate = addMonths(quarterMaxDate, 2);

  useEffect(() => {
    if (register) {
      if (register.status === "Реализовано") {
        setSelectedOption("Реализовано");
      } else if (register.status === "Нереализовано") {
        setSelectedOption("Није реализовано");
      }
      setStatus(register.status || "");
      const dateValue = register.natMeasureAdopByGovDate;
      if (dateValue) {
        try {
          let d: Date | null = null;

          if (typeof dateValue === "string") {
            const parsed = parse(dateValue, "dd/MM/yyyy", new Date());
            if (isValid(parsed)) {
              d = parsed;
            } else {
              const isoDate = new Date(dateValue);
              if (isValid(isoDate)) {
                d = isoDate;
              }
            }
          } else if (dateValue instanceof Date) {
            d = dateValue;
          }

          if (d) {
            if (d.getFullYear() < MIN_YEAR) {
              setDate(null);
            } else {
              setDate(d);
            }
          }
        } catch (e) {
          console.error("Date parsing error:", e);
        }
      }

      try {
        const { current } = getCurrentAndPreviousQuarter();

        if (Array.isArray(register.realisationStatusNotes)) {
          const currentNote = register.realisationStatusNotes.find((n: any) => {
            return n.quarter === quarter;
          });

          if (currentNote) {
            setNote(currentNote.name || "");
            setNoteId(currentNote.id || null);
          }
        }
      } catch (e) {
        console.error("Error setting note from quarter:", e);
      }
    }
  }, [register, quarter]);

  const isValidDate =
    date instanceof Date &&
    !isNaN(date.getTime()) &&
    date <= extendedMaxDate &&
    date >= extendedMinDate &&
    date.getFullYear() >= MIN_YEAR;

  const handleSave = () => {
    if (selectedOption === "Није реализовано") {
      handleSaveNote();
    } else {
      handleSaveRealised();
    }
  };

  const handleSaveNote = async () => {
    const noteResponse = await updateRealisationStatusNote({
      nationalMeasureId: parseInt(register.natMeasureId),
      name: note,
      id: noteId || undefined,
      reportId: register.id || undefined,
      quarter: quarter || undefined,
      status: register.status === "Реализовано" ? true : false,
    });

    if (noteResponse.status === 201) {
      onClose();
    } else {
      notif("Грешка при чувању напомене.", "error");
    }
  };

  const handleSaveRealised = async () => {
    if (date && date > extendedMaxDate) {
      notif(
        "Датум не може бити више од 2 месеца после краја квартала.",
        "error",
      );
      return;
    }

    if (date && date < extendedMinDate) {
      notif(
        "Датум не може бити више од 2 месеца пре почетка квартала.",
        "error",
      );
      return;
    }

    if (date && date.getFullYear() < MIN_YEAR) {
      notif(
        "Погрешан формат датума. Датум не може бити пре 1900. године.",
        "error",
      );
      return;
    }

    if (newRealisation) {
      setTempRealisation(date);
      setNoteData({
        nationalMeasureId: parseInt(register.natMeasureId),
        name: note,
        id: noteId || undefined,
      });
      callback();
      closeModal();
      
      return;
    }

    const resp = await updateRealisationDate({
      id: parseInt(register.natMeasureId),
      adopByGovDate: date,
      reportId: register.id,
    });

    await handleSaveNote();

    if (resp.status === 201) {
      notif("Измене су сачуване.", "success");
      
      if (onSavedRealisation) onSavedRealisation(register);
      else onClose();
    } else {
      notif("Грешка при чувању измена.", "error");
    }
  };

  const handleRemoveRealisation = async () => {
    const resp = await updateRealisationDate({
      id: parseInt(register.natMeasureId),
      adopByGovDate: null,
      reportId: register.id,
    });
    if (resp.status === 201) {
      setDate(null);
      notif("Датум реализације је успешно уклоњен.", "success");
      onClose();
    } else {
      notif("Грешка при уклањању реализације.", "error");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <span className="text-[#04438b] text-2xl font-bold">
          Измена реализације:
          {register?.natMeasureTitle || register?.name || "N/A"}
        </span>
      </DialogTitle>
      <div className="flex justify-start items-start flex-col ml-8 mt-3 gap-4">
        {newRealisation && (
          <FormControl>
            <FormLabel id="radio-select-label">Одаберите опцију</FormLabel>

            <div role="radiogroup">
              <FormControlLabel
                value="Реализовано"
                label="Реализовано"
                control={
                  <Radio
                    checked={selectedOption === "Реализовано"}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    tabIndex={0}
                  />
                }
              />

              <FormControlLabel
                value="Није реализовано"
                label="Није реализовано"
                control={
                  <Radio
                    checked={selectedOption === "Није реализовано"}
                    onChange={(e) => setSelectedOption(e.target.value)}
                    tabIndex={0}
                  />
                }
              />
            </div>
          </FormControl>
        )}
        {selectedOption === "Реализовано" && (
          <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={srLocale}
          >
            <DatePicker
              label="Датум усвајања"
              value={date}
              onChange={(newValue) => setDate(newValue)}
              inputFormat="dd/MM/yyyy"
              minDate={extendedMinDate}
              maxDate={extendedMaxDate}
              disablePast={false}
              disableFuture={false}
              defaultCalendarMonth={date || new Date()}
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!isValidDate && date !== null}
                  helperText={
                    !isValidDate && date !== null
                      ? "Датум мора бити унутар ±3 месеца од квартала"
                      : ""
                  }
                  sx={{ width: 300 }}
                />
              )}
              disabled={!selectedOption}
            />

            {!newRealisation && (
              <Button
                variant="outlined"
                onClick={() => {
                  setShowConfirmation(true);
                }}
                sx={{ mt: 2, borderColor: "#04438b", color: "#04438b" }}
                disabled={!selectedOption}
              >
                Уклони датум реализације
              </Button>
            )}
          </LocalizationProvider>
        )}
        <div>
          <div className="flex flex-col gap-1">
            <TextField
              label="Напомена"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              multiline
              minRows={3}
              maxRows={6}
              sx={{ width: 800, mt: 2 }}
              placeholder="Унесите напомену..."
              inputProps={{ maxLength: 300 }}
            />

            <div style={{ textAlign: "right", fontSize: 12, color: "#666" }}>
              {note.length}/300
            </div>
          </div>
          <Button
            variant="outlined"
            onClick={() => setShowNotesModal(true)}
            sx={{ mt: 2, borderColor: "#04438b", color: "#04438b" }}
          >
            Погледај све напомене
          </Button>
        </div>
      </div>

      <div className="flex flex-row justify-between items-center w-full mt-6 px-4 pb-4">
        <button
          className="border border-[#81848f] hover:bg-[#81848f] hover:text-white cursor-pointer flex items-center justify-center border-redError text-redError text-base font-semibold rounded-lg w-[250px] h-[55px]"
          onClick={() => onClose()}
        >
          Oткажи
        </button>
        <Tooltip
          title={disabled ? "Тренутно су закључане измене" : ""}
          arrow
          placement="top"
        >
          <span>
            <Button
              disabled={
                (selectedOption === "Реализовано" && !isValidDate) ||
                disabled ||
                !selectedOption
              }
              onClick={() => {
                handleSave();
              }}
              sx={{ backgroundColor: "#0F2765" }}
              className="bg-darkBlue cursor-pointer flex items-center justify-center text-white hover:opacity-90 text-base font-semibold rounded-lg w-[250px] h-[55px]"
              variant="contained"
            >
              {newRealisation ? "Даље" : `Сачувај`}
            </Button>
          </span>
        </Tooltip>
      </div>
      {showConfirmation && (
        <ConfirmationModal
          modalIsOpen={showConfirmation}
          closeModal={() => setShowConfirmation(false)}
          confirmation={handleRemoveRealisation}
          message="Да ли сте сигурни да желите да уклоните датум реализације?"
          subMessage="Уклањањем датума реализације статус ће се променити у нереализовано."
        />
      )}
      {showNotesModal && (
        <RealisationNotesTableModal
          open={showNotesModal}
          onClose={() => setShowNotesModal(false)}
          notes={register.realisationStatusNotes}
        />
      )}
    </Dialog>
  );
};

export default EditRealisationModal;
